import { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { StartingChatMessage } from '@eluve/blocks';

/**
 * Merges user custom starters with resolved defaults.
 * User starters appear first, then resolved defaults that aren't
 * already present (deduped by id).
 */
export function mergeStarters<T extends { id: string }>(
  userStarters: T[] | null | undefined,
  resolvedDefaults: T[],
): T[] {
  if (!userStarters || userStarters.length === 0) {
    return resolvedDefaults;
  }

  const userIds = new Set(userStarters.map((s) => s.id));
  const deduped = resolvedDefaults.filter((s) => !userIds.has(s.id));
  return [...userStarters, ...deduped];
}

/**
 * Fetches resolved conversation starter defaults from the API.
 * Returns the defaults array and loading state.
 */
export function useResolvedConversationStarters(tenantId: string): {
  starters: StartingChatMessage[];
  isLoading: boolean;
} {
  const apiClient = useApiClient();
  const [starters, setStarters] = useState<StartingChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      try {
        const response =
          await apiClient.conversationStarterDefaults.resolveForUser({
            params: { tenantId },
            query: {},
          });

        if (!cancelled && response.status === 200) {
          setStarters(response.body.starters as StartingChatMessage[]);
        }
      } catch {
        // network or unexpected error — fall back to empty starters
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetch();

    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId]);

  return { starters, isLoading };
}
